import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiService from '../services/api';
import Logger from '../utils/logger';
import Logo from '../components/Logo';
import { Button, Card, Spinner } from '../components/ui';
import { McpAutorisation, McpCapability } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';

/**
 * L'ecran de consentement OAuth du serveur MCP (plan MCP, palier 3, lot 2).
 *
 * C'est ici qu'une personne accorde a un client -- claude.ai, ChatGPT -- l'acces a UNE
 * de ses organisations, avec un sous-ensemble de droits. `/oauth/authorize` a valide la
 * demande et redirige ici ; cette page la montre, enregistre la decision et renvoie la
 * personne chez le client.
 *
 * Elle **n'emet aucun jeton**. La decision produit un code a usage unique, que le client
 * echangera lui-meme contre un jeton en fournissant sa preuve PKCE. Emettre ici
 * contournerait cette preuve, donc tout l'interet du mecanisme.
 *
 * Ce qui est affiche est L'HOTE de l'identifiant du client, jamais le nom qu'il se
 * donne : ce nom est auto-declare dans un document que n'importe qui peut publier, et
 * un attaquant y ecrirait « Claude ». L'hote, lui, est l'adresse d'ou le document a ete
 * lu -- la seule chose que l'identifiant ne peut pas mentir. La specification l'exige.
 *
 * L'API rend le meme 404 qu'une demande introuvable si le compte connecte n'est pas
 * celui qui l'a ouverte en premier : une adresse recopiee ne donne donc rien.
 */

// Le cast porte sur la FONCTION, comme dans McpServerPage : sans lui, l'inference
// generique de react-i18next deborde la limite de recursion de TypeScript (TS2589).
const traduire = useTranslation as unknown as (
  ns: string
) => { t: (cle: string, options?: Record<string, unknown>) => string };

const McpAuthorizePage: React.FC = () => {
  const { t } = traduire('mcp');
  const { identifiant } = useParams<{ identifiant: string }>();

  const [demande, setDemande] = useState<McpAutorisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [organisation, setOrganisation] = useState('');
  const [capacites, setCapacites] = useState<McpCapability[]>([]);

  /* `/oauth/authorize` renvoie ici avec `#erreur=<code>` quand il a refuse AVANT
     d'avoir valide l'adresse de retour du client -- le refus ne peut alors aller
     nulle part ailleurs sans ouvrir une redirection. Le code voyage dans le FRAGMENT :
     il ne part pas dans les journaux d'acces, et `App.tsx` ouvre le connecteur de
     depots des qu'un `?state=` ou `?provider=` apparait en query. */
  const erreurAmont = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.hash.replace(/^#/, '')).get('erreur')
    : null;

  const charger = useCallback(async () => {
    if (!identifiant) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await apiService.getMcpAutorisation(identifiant);
      setDemande(r);
      /* Une seule organisation : on la choisit, il n'y a pas de decision a prendre.
         Plusieurs : rien n'est pre-selectionne, pour que le choix soit un geste. */
      setOrganisation(r.organisations.length === 1 ? r.organisations[0].id : '');
      /* Les droits DEMANDES sont pre-coches, et c'est volontaire : la personne doit
         voir ce que le client reclame, et l'usage d'un ecran de consentement est de
         pouvoir REDUIRE. Ne rien cocher ferait accorder la lecture seule a qui vient
         justement connecter un agent, sans qu'il comprenne pourquoi ses outils
         d'ecriture ont disparu. Decocher reste a un clic. */
      setCapacites(r.portees_demandees);
      setError(null);
    } catch (err: unknown) {
      Logger.error('MCP authorize load failed', err);
      setError(t('autorisation.introuvable'));
    } finally {
      setLoading(false);
    }
  }, [identifiant, t]);

  useEffect(() => {
    charger();
  }, [charger]);

  const basculer = (c: McpCapability) =>
    setCapacites((avant) => (avant.includes(c) ? avant.filter((x) => x !== c) : [...avant, c]));

  const decider = async (accorde: boolean) => {
    if (!identifiant) return;
    if (accorde && !organisation) {
      setError(t('autorisation.choisirOrganisation'));
      return;
    }
    setEnvoi(true);
    setError(null);
    try {
      const r = await apiService.decideMcpAutorisation(identifiant, {
        accorde,
        organisation_id: accorde ? organisation : undefined,
        capacites: accorde ? capacites : undefined,
      });
      /* On quitte la console pour le client. `href` et non le routeur : l'adresse est
         chez lui, pas chez nous. */
      window.location.href = r.redirect_url;
    } catch (err: unknown) {
      Logger.error('MCP authorize decide failed', err);
      setError(getApiErrorMessage(err, t('autorisation.erreur')));
      setEnvoi(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-1">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-1 px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <Card>
          <Card.Header>
            <h1 className="text-[15px] font-semibold text-text-primary">
              {t('autorisation.titre')}
            </h1>
          </Card.Header>
          <Card.Body>
            {erreurAmont || !demande ? (
              <div className="space-y-2">
                <p className="text-[12px] text-text-primary">
                  {erreurAmont
                    ? t(`autorisation.refus.${erreurAmont}`, { defaultValue: t('autorisation.refus.inconnu') })
                    : error || t('autorisation.introuvable')}
                </p>
                <p className="text-[12px] text-text-secondary">{t('autorisation.refusAide')}</p>
              </div>
            ) : (
              <div className="space-y-5">
                <p className="text-[12px] text-text-primary">
                  {/* L'hote, en evidence : c'est la seule chose sur quoi la personne
                      peut fonder sa decision. */}
                  <span className="font-semibold">{demande.client_hote}</span>{' '}
                  {t('autorisation.intro')}
                </p>

                <div>
                  <label className="block text-[12px] text-text-secondary mb-1">
                    {t('autorisation.organisation')}
                  </label>
                  <select
                    className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-[12px] text-text-primary"
                    value={organisation}
                    onChange={(e) => setOrganisation(e.target.value)}
                    disabled={envoi}
                  >
                    <option value="">{t('autorisation.choisir')}</option>
                    {demande.organisations.map((o) => (
                      <option key={o.id} value={o.id}>{o.nom}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <p className="text-[12px] text-text-secondary mb-2">
                    {t('autorisation.droits')}
                  </p>
                  <div className="space-y-2">
                    {demande.portees_possibles.map((c) => (
                      <label key={c} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-[3px]"
                          checked={capacites.includes(c)}
                          onChange={() => basculer(c)}
                          disabled={envoi}
                        />
                        <span>
                          <span className="text-[12px] text-text-primary">
                            {t(`emission.${c}`)}
                          </span>
                          {demande.portees_demandees.includes(c) && (
                            <span className="ml-2 text-[11px] text-text-tertiary">
                              {t('autorisation.demande')}
                            </span>
                          )}
                          <span className="block text-[11px] text-text-tertiary">
                            {t(`emission.${c}Aide`)}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <p className="text-[12px] text-text-tertiary">
                  {t('emission.pasDeDestruction')}
                </p>

                {error && <p className="text-[12px] text-danger">{error}</p>}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    disabled={envoi}
                    onClick={() => decider(false)}
                  >
                    {t('autorisation.refuser')}
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    disabled={envoi}
                    onClick={() => decider(true)}
                  >
                    {envoi ? t('autorisation.envoi') : t('autorisation.autoriser')}
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default McpAuthorizePage;
